import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { BackendHealthService } from '../backend-health/backend-health.service';
import { environment } from '../../../environments/environment';

/**
 * HTTP Interceptor que maneja errores HTTP globalmente.
 * - 401: Redirige a login
 * - 403: Error de permisos
 * - 500+: Error del servidor
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const backendHealth = inject(BackendHealthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'Ha ocurrido un error';

      switch (error.status) {
        case 401:
          // No autorizado
          console.warn('401 No autorizado:', req.url);
          // NO redirigir automáticamente - dejar que cada componente maneje el error.
          // /api/auth/logout maneja su propio 401 internamente (ver AuthService.logout),
          // así que NO lo tocamos aquí para evitar doble clearSession.
          // Solo limpiar sesión si es el endpoint /me (que usamos para validar sesión).
          if (req.url.includes('/api/auth/me')) {
            authService.logoutLocal();
          }
          errorMessage = 'No autorizado. Verifique su sesión.';
          break;

        case 403:
          // Prohibido - no tiene permisos
          console.warn('Acceso denegado:', req.url);
          errorMessage = 'No tiene permisos para realizar esta acción.';
          break;

        case 404:
          // No encontrado
          console.warn('Recurso no encontrado:', req.url);
          errorMessage = 'El recurso solicitado no fue encontrado.';
          break;

        case 409:
          // Conflicto (ej: sesión activa en otro dispositivo)
          console.warn('Conflicto:', error.error);
          // El backend puede devolver { detail: { message: "..." } } o { detail: "..." }
          if (typeof error.error?.detail === 'string') {
            errorMessage = error.error.detail;
          } else if (typeof error.error?.detail?.message === 'string') {
            errorMessage = error.error.detail.message;
          } else if (typeof error.error?.message === 'string') {
            errorMessage = error.error.message;
          } else {
            errorMessage = 'Ya tiene una sesión activa. Intente más tarde.';
          }
          break;

        case 429:
          // Rate limit exceeded
          console.warn('Rate limit exceeded:', req.url);
          const retryAfter = error.headers?.get('Retry-After');
          errorMessage = retryAfter
            ? `Demasiados intentos. Intente en ${retryAfter} segundos.`
            : 'Demasiados intentos. Intente más tarde.';
          break;

        case 422:
          // Error de validación
          console.warn('Error de validación:', error.error);
          if (typeof error.error?.detail === 'string') {
            errorMessage = error.error.detail;
          } else if (Array.isArray(error.error?.detail)) {
            // FastAPI validation errors come as array
            errorMessage = error.error.detail.map((e: any) => e.msg || e.message).join('. ');
          } else if (typeof error.error?.message === 'string') {
            errorMessage = error.error.message;
          } else {
            errorMessage = 'Error de validación en los datos enviados.';
          }
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Error del servidor
          console.error('Error del servidor:', error);
          errorMessage =
            'Error en el servidor. Por favor, intente más tarde.';
          break;

        case 0: {
          // Status 0 = network failure / CORS preflight failure / DNS / VPN.
          // Para el usuario es indistinguible — todos significan "no llegue
          // al backend". Lo loggeamos explicito y notificamos al
          // BackendHealthService para que dispare snackbar global una vez
          // (deduplicado para no spamear si fallan multiples requests).
          console.error(
            `[HTTP] ${req.method} ${req.url} -> status 0 (sin respuesta del backend).`,
            `apiUrl configurado=${environment.apiUrl}. Causas comunes: VPN desconectada,`,
            `VM CRE-PUNTOMAS-BACK01 apagada, container puntomas-api-* en restart loop, o CORS roto.`,
            error
          );
          backendHealth.reportDown();
          errorMessage =
            'No se pudo conectar con el servidor. Verifique su conexión / VPN.';
          break;
        }

        default:
          // Otros errores
          console.error('Error HTTP:', error.status, error.message);
          if (typeof error.error?.detail === 'string') {
            errorMessage = error.error.detail;
          } else if (typeof error.error?.message === 'string') {
            errorMessage = error.error.message;
          } else if (typeof error.message === 'string') {
            errorMessage = error.message;
          }
      }

      // Retornar el error con el mensaje procesado
      return throwError(() => ({
        ...error,
        userMessage: errorMessage
      }));
    })
  );
};
